import z from 'zod';
import { CP_SESSION_KEY } from '@/constants/constants';
import { useCustomerAuthStore } from '@/stores/customer-auth-store';

const USER_TOKEN_KEY = 'com.toyota.tme.user.token';
const USER_CUSTOMER_PROFILE_KEY = 'com.toyota.tme.user.customerProfile';
const USER_TOKEN_EXPIRES_AT_KEY = 'com.toyota.tme.user.token_expires_at';

export function isCustomerAuthenticated() {
	if (typeof localStorage === 'undefined') {
		return false;
	}

	syncAuthenticationCookie();

	const token = localStorage.getItem(USER_TOKEN_KEY);

	if (!token) {
		return false;
	}

	if (isCustomerAuthenticationTokenExpired()) {
		return false;
	}

	const authToken = getCPSessionCookie();
	if (!authToken) {
		return false;
	}

	return true;
}

type Address = { addressLine1: string; addressLine2: string; city: string; country: string; postcode: string; type: string };
type CommPref = { phones: Array<{ phone: string; preferred: boolean }> | false };

export type CustomerProfile = {
	email: string;
	firstName: string;
	lastName: string;
	username: string;
	myToyotaId: string;
	uuid: string;
	mobileNo: string | null;
	addresses: Array<Address>;
	commPref: CommPref;
};

export type AuthenticatedCustomer = { user: CustomerProfile; token: string };
export function getAuthenticatedCustomer(): AuthenticatedCustomer | undefined {
	if (typeof localStorage === 'undefined') {
		return undefined;
	}

	syncAuthenticationCookie();

	const token = localStorage.getItem(USER_TOKEN_KEY);
	const user = localStorage.getItem(USER_CUSTOMER_PROFILE_KEY);

	if (!token || !user) {
		return undefined;
	}

	if (isCustomerAuthenticationTokenExpired()) {
		return undefined;
	}

	const authToken = getCPSessionCookie();
	if (!authToken) {
		return undefined;
	}

	const userData = JSON.parse(user);

	if (!userData) {
		return undefined;
	}

	return { user: userData, token };
}

function isCustomerAuthenticationTokenExpired(): boolean {
	if (typeof localStorage === 'undefined') {
		return true;
	}

	const expiresAt = Number.parseInt(localStorage.getItem(USER_TOKEN_EXPIRES_AT_KEY) || '');

	if (!expiresAt) {
		return true;
	}

	if (expiresAt <= Date.now()) {
		return true;
	}

	return false;
}

/**
 * Logs out the customer and triggers a callback function when the logout is successful.
 * We have to do it this way as this is how CP(Customer Portal) handles it.
 * @param callback - The callback function to be executed after the logout is successful.
 *
 */
export function customerSignOut(callback?: () => void) {
	document.cookie = `${CP_SESSION_KEY}=; path=/; max-age=0`;
	window?.postal?.publish({ topic: 'sso.logout.clicked', channel: 'com.toyota.tme.user' });
	// Clear localStorage incase publishing the logout event fails (will happen for SSO most likely)
	localStorage.removeItem(USER_TOKEN_KEY);
	localStorage.removeItem(USER_CUSTOMER_PROFILE_KEY);
	localStorage.removeItem(USER_TOKEN_EXPIRES_AT_KEY);
	useCustomerAuthStore.getState().clearAuth();
	callback?.();
}

const PATHS_TO_REMAIN_ON_WHEN_LOGGING_OUT = [
	'configurator',
	'basket',
	'see-earlier-available-cars',
	'see-earlier-available-cars/vehicle'
] as const;

const PATHS_TO_WARN_YOULL_LOSE_PROGRESS_ON_WHEN_LOGGING_OUT = ['checkout'] as const;

function selectRetailerPageRoute(locale: string) {
	return `/${locale}`;
}

/**
 * Returns the route to push to after logout and whether to show the "you will lose progress" notification.
 * If the route returned is undefined you should remain on the current page.
 *
 * @param pathname - The current path the browser is on.
 * @param locale - The locale code (e.g. 'es-es').
 * @param retailerId - The id of the selected retailer.
 */
export function getLogoutRedirectRoute(
	pathname: string,
	locale: string,
	retailerId?: string | undefined
): { route: string | undefined; shouldPromptWarning: boolean } {
	// Go to select retailer if no retailer selected
	if (!retailerId) {
		return { route: selectRetailerPageRoute(locale), shouldPromptWarning: false };
	}

	// Stay on select retailer page if already on it
	if (pathname === selectRetailerPageRoute(locale)) {
		return { route: selectRetailerPageRoute(locale), shouldPromptWarning: false };
	}

	// Go to retailer select if route not in paths to remain on list
	const routePostLogout = PATHS_TO_REMAIN_ON_WHEN_LOGGING_OUT.some((postRetailerPath) =>
		pathname.startsWith(`/${locale}/${retailerId}/${postRetailerPath}`)
	)
		? undefined
		: `/${locale}`;

	const showProgressWarning = PATHS_TO_WARN_YOULL_LOSE_PROGRESS_ON_WHEN_LOGGING_OUT.some((postRetailerPath) =>
		pathname.startsWith(`/${locale}/${retailerId}/${postRetailerPath}`)
	);

	return { route: routePostLogout, shouldPromptWarning: showProgressWarning };
}

/**
 * Login credentials get saved to local storage but we need to able
 * to pass the token when loading pages.  To do this is needs to be in cookie
 * form.  We take the token and email from the login success event and
 * turn them into a cookie.
 *
 *  * @param loginSuccessEvent - The event returned from the sso.login.ok topic.
 */
export function saveLoginCredentailsAsCookie(loginSuccessEvent: unknown) {
	const parsedLoginEvent = loginSuccessEventSchema.parse(loginSuccessEvent);
	setCPSessionCookie(parsedLoginEvent.userData.token, parsedLoginEvent.userData.customerProfile.email);
	// Sync customer auth store with localStorage
	const authData = getAuthenticatedCustomer();
	useCustomerAuthStore.getState().syncFromStorage(authData);
}

const CP_SESSION_MAX_AGE = 60 * 60 * 2; // cp expiry time is 2 hours

const loginSuccessEventSchema = z.object({ userData: z.object({ token: z.string(), customerProfile: z.object({ email: z.string() }) }) });

/**
 * Sets the CP session cookie with the given token and email.
 * @param token - The authentication token.
 * @param email - The customer's email address.
 */
function setCPSessionCookie(token: string, email: string): void {
	const cookieValue = encodeURIComponent(JSON.stringify({ token, email }));
	document.cookie = `${CP_SESSION_KEY}=${cookieValue}; path=/; max-age=${CP_SESSION_MAX_AGE}`;
}

/**
 * CP SSO will set the token in localStorage while it's outside of our app and doesn't provide a callback for when the token is set via the CP Module, so we need to sync the cookie with localStorage whenver we check for authentication/user profile
 * Synchronizes the CP session cookie with localStorage data.
 * If localStorage has valid auth data but the cookie is missing or mismatched,
 * the cookie will be regenerated from localStorage.
 */
function syncAuthenticationCookie(): void {
	const token = localStorage.getItem(USER_TOKEN_KEY);
	const userJson = localStorage.getItem(USER_CUSTOMER_PROFILE_KEY);

	if (!token || !userJson) {
		return;
	}

	let email: string;
	try {
		const userData = JSON.parse(userJson);
		email = userData?.email;
		if (!email) {
			return;
		}
	} catch {
		return;
	}

	const cookieValue = getCPSessionCookie();

	if (!cookieValue) {
		setCPSessionCookie(token, email);
		return;
	}

	try {
		const cookieData = JSON.parse(decodeURIComponent(cookieValue));
		if (cookieData?.token !== token || cookieData?.email !== email) {
			setCPSessionCookie(token, email);
		}
	} catch {
		setCPSessionCookie(token, email);
	}
}

function getCPSessionCookie(): string | null {
	const value = `; ${document.cookie}`;
	const parts = value.split(`; ${CP_SESSION_KEY}=`);
	if (parts.length === 2) {
		return parts.pop()!.split(';').shift() || null;
	}
	return null;
}
